import { FC, useState } from 'react';

import { ConfirmModal } from './ConfirmModal';
import { OffersGrid } from './OffersGrid';
import { OffersList } from './OffersList';
import { PremiumExtensionDialog } from './PremiumExtensionDialog';
import { Offer, OfferProperty, PremiumSummary } from '../../../../types';
import { ConfirmModalState, ViewMode } from '../../../../types/ui';
import { activatePremiumForOffer } from '../../premiumRequests';

interface Props {
    offers: Offer[];
    offerProperties: OfferProperty[];
    loadedPages: number;
    premiumSummary: PremiumSummary;
    selectedOffers: number[];
    loading: boolean;
    viewMode: ViewMode;
    setSelectedOffers: (offerId: number[]) => void;
    confirmModalState: ConfirmModalState;
    setConfirmModalState: (open: ConfirmModalState) => void;
    onPremiumActivated: (offer: Offer, premiumSummary: PremiumSummary) => void;
}

export const Offers: FC<Props> = ({
    offers,
    offerProperties,
    loadedPages,
    premiumSummary,
    selectedOffers,
    loading,
    viewMode,
    setSelectedOffers,
    confirmModalState,
    setConfirmModalState,
    onPremiumActivated,
}) => {
    const [activatingOfferId, setActivatingOfferId] = useState<number | null>(
        null,
    );
    const [premiumActivationOffer, setPremiumActivationOffer] =
        useState<Offer | null>(null);
    const [premiumErrors, setPremiumErrors] = useState<Record<number, string>>(
        {},
    );

    const toggleOfferSelection = (id: number) => {
        if (selectedOffers.includes(id)) {
            setSelectedOffers(
                selectedOffers.filter((offerId) => offerId !== id),
            );
        } else {
            setSelectedOffers([...selectedOffers, id]);
        }
    };

    const activatePremium = async (offer: Offer) => {
        setActivatingOfferId(offer.id);
        setPremiumErrors((current) => {
            const nextErrors = { ...current };
            delete nextErrors[offer.id];
            return nextErrors;
        });

        try {
            const result = await activatePremiumForOffer(offer.id);
            onPremiumActivated(result.offer, result.premiumSummary);
            setPremiumActivationOffer(null);
        } catch (error) {
            setPremiumErrors((current) => ({
                ...current,
                [offer.id]:
                    error instanceof Error
                        ? error.message
                        : 'Не вдалося активувати premium.',
            }));
        } finally {
            setActivatingOfferId(null);
        }
    };

    const requestPremiumActivation = (offer: Offer) => {
        setPremiumErrors((current) => {
            const nextErrors = { ...current };
            delete nextErrors[offer.id];
            return nextErrors;
        });
        setPremiumActivationOffer(offer);
    };

    return (
        <>
            {confirmModalState.open && (
                <ConfirmModal
                    state={confirmModalState}
                    setState={setConfirmModalState}
                />
            )}
            <PremiumExtensionDialog
                offer={premiumActivationOffer}
                loading={activatingOfferId === premiumActivationOffer?.id}
                error={
                    premiumActivationOffer
                        ? premiumErrors[premiumActivationOffer.id] || null
                        : null
                }
                onOpenChange={(open) => {
                    if (!open) {
                        setPremiumActivationOffer(null);
                    }
                }}
                onConfirm={() => {
                    if (premiumActivationOffer) {
                        void activatePremium(premiumActivationOffer);
                    }
                }}
            />
            {viewMode === 'grid' ? (
                <OffersGrid
                    offers={offers}
                    loadedPages={loadedPages}
                    premiumSummary={premiumSummary}
                    selectedOffers={selectedOffers}
                    loading={loading}
                    toggleOfferSelection={toggleOfferSelection}
                    setConfirmModalState={setConfirmModalState}
                    activatingOfferId={activatingOfferId}
                    onPremiumActivationRequest={requestPremiumActivation}
                />
            ) : (
                <OffersList
                    offers={offers}
                    offerProperties={offerProperties}
                    premiumSummary={premiumSummary}
                    selectedOffers={selectedOffers}
                    toggleOfferSelection={toggleOfferSelection}
                    setConfirmModalState={setConfirmModalState}
                    activatingOfferId={activatingOfferId}
                    premiumErrors={premiumErrors}
                    onPremiumActivationRequest={requestPremiumActivation}
                />
            )}
        </>
    );
};
